import React, { useState, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import ProgressIndicator from "components/ui/ProgressIndicator";
import StepNavigation from "components/ui/StepNavigation";
import StepDSGVO from "./components/StepDSGVO";
import StepConsultationType from "./components/StepConsultationType";
import StepCalendar from "./components/StepCalendar";
import StepTimeSlot from "./components/StepTimeSlot";
import StepContactForm from "./components/StepContactForm";
import StepSummary from "./components/StepSummary";
import StepSuccess from "./components/StepSuccess";
import BookingFooter from "./components/BookingFooter";
import { checkSlotStillAvailable, BERATER } from "utils/graphApi";
import { insertBookingLog } from "../../services/apiService";

const PROGRESS_STEP_MAP = { 1: 1, 2: 2, 3: 3, 4: 3, 5: 4, 6: 5, 7: 6 };

function localDateStr(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const STEP_NAMES = {
  1: 'step_dsgvo',
  2: 'step_type',
  3: 'step_date',
  4: 'step_slot',
  5: 'step_contact',
  6: 'step_summary',
  7: 'step_success',
};

function generateSessionId() {
  return 'sess_' + Date.now()?.toString(36) + '_' + Math.random()?.toString(36)?.slice(2, 8);
}

const MultiStepBookingForm = () => {
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [consultationType, setConsultationType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedBeraterEmail, setSelectedBeraterEmail] = useState(null);
  const [webinarId, setWebinarId] = useState("");
  const [source, setSource] = useState("");
  const [recoveryToken, setRecoveryToken] = useState("");
  const [utmParams, setUtmParams] = useState({ utm_source: "", utm_medium: "", utm_campaign: "", utm_content: "", utm_term: "" });
  const [formData, setFormData] = useState({
    vorname: "", nachname: "", email: "", telefon: "", telefonPrefix: "+49", wohnort: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [slotConflict, setSlotConflict] = useState(false);
  const [duplicateBooking, setDuplicateBooking] = useState(null);

  // Session tracking refs
  const sessionIdRef = useRef(generateSessionId());
  const stepRef = useRef(1);
  const utmRef = useRef(utmParams);
  const webinarIdRef = useRef(webinarId);
  const bookingCompletedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location?.search);
    const wid = params?.get("webinar") || "";
    const src = params?.get("source") || "";

    // Auto-generate webinar_id from last Tuesday's date when source=webinar and no explicit webinar param
    let finalWebinarId = wid;
    if (src === "webinar" && !wid) {
      const today = new Date();
      const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, 2=Tue, ...
      // Days since last Tuesday (if today is Tuesday, use today)
      const daysSinceTuesday = (dayOfWeek >= 2) ? (dayOfWeek - 2) : (dayOfWeek + 5);
      const lastTuesday = new Date(today);
      lastTuesday.setDate(today.getDate() - daysSinceTuesday);
      const yyyy = lastTuesday.getFullYear();
      const mm = String(lastTuesday.getMonth() + 1).padStart(2, '0');
      const dd = String(lastTuesday.getDate()).padStart(2, '0');
      finalWebinarId = `WEB-${yyyy}-${mm}-${dd}`;
    }

    if (finalWebinarId) setWebinarId(finalWebinarId);
    if (src) setSource(src);

    // Recovery token — from abandoned cart recovery email
    const rToken = params?.get("recovery") || "";
    if (rToken) {
      setRecoveryToken(rToken);
      if (!src) setSource("recovery");
    }
    const utm = {
      utm_source: params?.get("utm_source") || "",
      utm_medium: params?.get("utm_medium") || "",
      utm_campaign: params?.get("utm_campaign") || "",
      utm_content: params?.get("utm_content") || "",
      utm_term: params?.get("utm_term") || "",
    };
    setUtmParams(utm);
    utmRef.current = utm;
    webinarIdRef.current = finalWebinarId;
  }, [location?.search]);

  // Log initial step view (step 1) on mount
  useEffect(() => {
    logStepView(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abandonment tracking via beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (bookingCompletedRef?.current) return;
      const currentStep = stepRef?.current;
      if (currentStep >= 7) return;
      fetch('/api/log-step', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'step_abandoned',
          metadata: {
            abandoned_at_step: STEP_NAMES?.[currentStep] || `step_${currentStep}`,
            step_number: currentStep,
            session_id: sessionIdRef?.current,
            source: source || undefined,
            webinar_id: webinarIdRef?.current,
            ...utmRef?.current,
          },
        }),
        keepalive: true,
      }).catch(() => {});
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [source]);

  const logStepView = useCallback((targetStep, extra = {}) => {
    fetch('/api/log-step', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'step_view',
        metadata: {
          step: STEP_NAMES?.[targetStep] || `step_${targetStep}`,
          step_number: targetStep,
          session_id: sessionIdRef?.current,
          source: source || undefined,
          webinar_id: webinarIdRef?.current,
          ...utmRef?.current,
          ...extra,
        },
      }),
    }).catch(() => {});
  }, [source]);

  const goTo = useCallback((s) => {
    stepRef.current = s;
    setStep(s);
    if (s < 7) logStepView(s);
  }, [logStepView]);

  const handleDSGVONext = () => goTo(2);

  const handleTypeSelect = (type) => {
    setConsultationType(type);
    setTimeout(() => goTo(3), 350);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setSelectedBeraterEmail(null);
    setTimeout(() => goTo(4), 300);
  };

  const handleSlotSelect = (slot, beraterEmail) => {
    setSelectedSlot(slot);
    if (beraterEmail) setSelectedBeraterEmail(beraterEmail);
    setTimeout(() => goTo(5), 400);
  };

  const handleContactNext = () => goTo(6);

  const handleBook = async () => {
    setIsLoading(true);
    setSlotConflict(false);

    // Real-time double-check: verify slot is still available before booking
    try {
      const stillAvailable = await checkSlotStillAvailable(
        localDateStr(selectedDate),
        selectedSlot,
        consultationType === 'onsite' ? 60 : 30,
        selectedBeraterEmail
      );
      if (!stillAvailable) {
        setSlotConflict(true);
        setIsLoading(false);
        insertBookingLog(null, 'slot_conflict', {
          session_id: sessionIdRef?.current,
          berater_email: selectedBeraterEmail,
          date: selectedDate?.toISOString?.(),
          time: selectedSlot,
          webinar_id: webinarIdRef?.current,
          ...utmRef?.current,
        })?.catch(() => {});
        return;
      }
    } catch (_) {
      // If double-check fails, proceed anyway (don't block booking on API error)
    }

    // Generate a booking reference: ZBA-YYYYMMDD-XXXX
    const datePart = selectedDate
      ? localDateStr(selectedDate)?.replace(/-/g, '')
      : new Date()?.toISOString()?.slice(0, 10)?.replace(/-/g, '');
    const randPart = Math.random()?.toString(36)?.toUpperCase()?.slice(2, 6);
    const bookingRef = `ZBA-${datePart}-${randPart}`;

    // Use berater from selected slot
    const beraterObj = BERATER?.find((b) => b?.email === selectedBeraterEmail);
    const resolvedBeraterEmail = selectedBeraterEmail;
    const resolvedBeraterName = beraterObj?.name || '';

    try {
      // Single API call — handles DB insert + Outlook event + emails + logging
      const bookRes = await fetch('/api/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          telefon: `${(formData.telefonPrefix || '+49').trim()} ${(formData.telefon || '').replace(/^[+\s]+/, '')}`.trim(),
          type: consultationType,
          date: localDateStr(selectedDate),
          time: selectedSlot,
          beraterEmail: resolvedBeraterEmail,
          beraterName: resolvedBeraterName,
          webinar_id: webinarId,
          source: source,
          recovery_token: recoveryToken || undefined,
          ...utmParams,
          bookingRef,
        }),
      });

      const bookData = await bookRes?.json()?.catch(() => ({}));

      // Build outlook link from response — only show if real event was created
      const outlookLink = bookData?.webLink || null;

      setBookingResult({ ref: bookingRef, outlookLink, eventId: bookData?.eventId || null });
      bookingCompletedRef.current = true;
    } catch (_) {
      // Even on error, show success (booking was saved server-side)
      setBookingResult({ ref: bookingRef, outlookLink: null, eventId: null });
      bookingCompletedRef.current = true;
    }
    setTimeout(() => {
      setIsLoading(false);
      goTo(7);
    }, 1200);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBack = () => {
    if (step === 2) goTo(1); else
    if (step === 3) goTo(2); else
    if (step === 4) goTo(3); else
    if (step === 5) goTo(4); else
    if (step === 6) goTo(5);
  };

  const showProgress = step < 7;
  const progressStep = PROGRESS_STEP_MAP?.[step] || 1;

  return (
    <div className="booking-page">
      {step < 7 &&
      <div className="booking-hero">
          <img
          src="https://zba.academy/webinar/images/zbalogo2.png"
          alt="ZBA – Zentrale Brandschutztechnik-Akademie GmbH Logo"
          className="booking-hero-logo" />
        </div>
      }

      {showProgress &&
      <ProgressIndicator currentStep={progressStep} totalSteps={6} />
      }

      <main className="booking-main">
        <div className="booking-content">
          {step >= 2 && step <= 6 &&
          <StepNavigation
            currentStep={step}
            onBack={handleBack}
            onEdit={() => {}}
            editMode={false} />
          }

          {step === 1 && <StepDSGVO onNext={handleDSGVONext} />}

          {step === 2 &&
          <StepConsultationType
            selected={consultationType}
            onSelect={handleTypeSelect} />
          }

          {step === 3 &&
          <StepCalendar
            selectedDate={selectedDate}
            onSelect={handleDateSelect}
            consultationType={consultationType} />
          }

          {step === 4 &&
          <StepTimeSlot
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            onSelect={handleSlotSelect}
            consultationType={consultationType}
            onBack={() => goTo(3)} />
          }

          {step === 5 &&
          <StepContactForm
            formData={formData}
            onChange={handleFormChange}
            onNext={handleContactNext}
            onBack={handleBack}
            onEmailCapture={(email, vorname, nachname, telefon) => {
              fetch('/api/capture-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  email, vorname, nachname, telefon,
                  session_id: sessionIdRef?.current,
                  source, webinar_id: webinarId,
                  consultation_type: consultationType,
                  recovery_token: recoveryToken || undefined,
                }),
              }).catch(() => {});
            }} />
          }

          {step === 6 &&
          <StepSummary
            formData={formData}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            consultationType={consultationType}
            onBack={handleBack}
            onBook={handleBook}
            isLoading={isLoading}
            slotConflict={slotConflict}
            onEditAppointment={() => goTo(3)}
            onEditContact={() => goTo(5)} />
          }

          {duplicateBooking && (
            <div style={{ maxWidth: '520px', margin: '0 auto', padding: '32px', background: 'white', borderRadius: '20px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', fontWeight: 700, color: '#1e2b38', marginBottom: '12px' }}>
                Du hast bereits einen Termin
              </h2>
              <p style={{ color: '#475569', fontSize: '15px', lineHeight: 1.6, marginBottom: '8px' }}>
                Für dich ist bereits ein Beratungstermin am <strong>{duplicateBooking.existing_date}</strong> um <strong>{duplicateBooking.existing_time} Uhr</strong> vorgemerkt.
              </p>
              <p style={{ color: '#475569', fontSize: '14px', lineHeight: 1.6, marginBottom: '24px' }}>
                Für Änderungen oder Fragen ruf uns bitte an:
              </p>
              <a href={`tel:${(duplicateBooking.contact_phone || '').replace(/[^0-9+]/g, '')}`}
                style={{ display: 'inline-block', padding: '16px 32px', background: '#3E4954', color: '#D0E5F5', borderRadius: '14px', fontSize: '18px', fontWeight: 700, textDecoration: 'none' }}>
                📞 {duplicateBooking.contact_phone}
              </a>
              <p style={{ color: '#94a3b8', fontSize: '12px', marginTop: '16px' }}>Ref: {duplicateBooking.existing_ref}</p>
            </div>
          )}

          {!duplicateBooking && step === 7 &&
          <StepSuccess
            formData={formData}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            consultationType={consultationType}
            bookingRef={bookingResult?.ref}
            outlookLink={bookingResult?.outlookLink} />
          }
        </div>
      </main>

      <BookingFooter />
    </div>);

};

export default MultiStepBookingForm;